import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@dune/ui";
import { AlertTriangle, CheckCircle, Info, XCircle } from "lucide-react";
import React from "react";

export type ConfirmationVariant = "default" | "danger" | "warning" | "success";

interface ConfirmationDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: string;
	description: string;
	confirmText?: string;
	cancelText?: string;
	variant?: ConfirmationVariant;
	onConfirm: () => void | Promise<void>;
	isLoading?: boolean;
}

const getVariantIcon = (variant: ConfirmationVariant) => {
	switch (variant) {
		case "danger":
			return <XCircle className="h-6 w-6 text-red-500" />;
		case "warning":
			return <AlertTriangle className="h-6 w-6 text-orange-500" />;
		case "success":
			return <CheckCircle className="h-6 w-6 text-green-500" />;
		default:
			return <Info className="h-6 w-6 text-blue-500" />;
	}
};

const getVariantButtonProps = (variant: ConfirmationVariant) => {
	switch (variant) {
		case "danger":
			return { variant: "destructive" as const };
		case "warning":
			return {
				variant: "default" as const,
				className: "bg-orange-600 hover:bg-orange-700",
			};
		case "success":
			return {
				variant: "default" as const,
				className: "bg-green-600 hover:bg-green-700",
			};
		default:
			return { variant: "default" as const };
	}
};

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
	open,
	onOpenChange,
	title,
	description,
	confirmText = "Confirmer",
	cancelText = "Annuler",
	variant = "default",
	onConfirm,
	isLoading = false,
}) => {
	const handleConfirm = async () => {
		await onConfirm();
	};

	const handleCancel = () => {
		if (!isLoading) {
			onOpenChange(false);
		}
	};

	const icon = getVariantIcon(variant);
	const buttonProps = getVariantButtonProps(variant);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<div className="flex items-center gap-3">
						{icon}
						<DialogTitle>{title}</DialogTitle>
					</div>
					<DialogDescription className="pt-2">{description}</DialogDescription>
				</DialogHeader>

				<DialogFooter className="gap-2">
					<Button
						type="button"
						variant="outline"
						onClick={handleCancel}
						disabled={isLoading}
					>
						{cancelText}
					</Button>
					<Button
						type="button"
						{...buttonProps}
						onClick={handleConfirm}
						disabled={isLoading}
						isLoading={isLoading}
					>
						{confirmText}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default ConfirmationDialog;
